// src/admin/pages/EditProduct.tsx
import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { 
  FaImage, FaEdit, FaBoxOpen, FaRupeeSign, 
  FaLayerGroup, FaClipboardList 
} from 'react-icons/fa';
import Notification from '../components/Notification/Notification';
import { useParams, useNavigate } from 'react-router-dom';

// --- Interfaces ---
interface Category {
  id: number;
  name: string;
  icon: string;
  parentId: number | null;
}

interface CategoryType {
  id: number;
  name: string;
}

interface Product {
  id?: number;
  productName: string;
  productCode?: string;
  description: string;
  price: number;
  discount: number;
  bulkDiscountPrice?: number;
  tax: number;
  offer?: string;
  imageUrl?: string;
  category: Category;
  categoryType: CategoryType;
  stockQuantity: number;
  typeQuantity: number;
  measurementUnit: string;
  status: string;
}

const EditProduct: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // --- State Variables ---
  const [productName, setProductName] = useState<string>('');
  const [productCode, setProductCode] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [price, setPrice] = useState<string>('0.00');
  const [discount, setDiscount] = useState<string>('0');
  const [bulkDiscountPrice, setBulkDiscountPrice] = useState<string>('0.00');
  const [taxRate, setTaxRate] = useState<string>('0');
  const [offer, setOffer] = useState<string>('');
  
  // Image
  const [imageUrl, setImageUrl] = useState<string>(''); 
  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);

  // Inventory & Meta
  const [stockQuantity, setStockQuantity] = useState<string>('0');
  const [typeQuantity, setTypeQuantity] = useState<string>('0.00');
  const [status, setStatus] = useState<string>('active');

  // Categories & Units
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [categoryTypes, setCategoryTypes] = useState<CategoryType[]>([]);
  const [selectedCategoryType, setSelectedCategoryType] = useState<CategoryType | null>(null);
  
  // Units State
  const [measurementUnits, setMeasurementUnits] = useState<string[]>([]);
  const [selectedMeasurementUnit, setSelectedMeasurementUnit] = useState<string>('');

  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info'; } | null>(null);

  // --- Helpers ---
  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>, setter: React.Dispatch<React.SetStateAction<string>>) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) setter(value);
  };

  // --- LOGIC FIX: Hardcoded Units matching CreateProduct Page ---
  const getUnitsForType = (typeName: string) => {
    const lowerName = typeName.toLowerCase().trim();
    if (lowerName === 'liquid') {
        return ['ml', 'litre'];
    } 
    else if (lowerName === 'capsule' || lowerName === 'tablets' || lowerName === 'tablet') {
        return ['pieces', 'strips'];
    } 
    else if (lowerName === 'powder') {
        return ['gm', 'kg'];
    } 
    else {
        return ['pieces', 'kg', 'gm', 'ml', 'litre']; 
    }
  };

  // --- 1. Initial Data Load ---
  useEffect(() => {
    const initData = async () => {
      try {
        // Fetch Metadata
        const [catRes, typeRes] = await Promise.all([
          fetch('https://ayushvatika.com/api/categories'),
          fetch('https://ayushvatika.com/api/category-types')
        ]);
        setCategories(await catRes.json());
        setCategoryTypes(await typeRes.json());

        // Fetch Product Data
        if (id) {
          const prodRes = await fetch(`https://ayushvatika.com/api/products/${id}`);
          if (!prodRes.ok) throw new Error('Product not found');
          const product: Product = await prodRes.json();

          // Fill Form
          setProductName(product.productName);
          setProductCode(product.productCode || '');
          setDescription(product.description);
          setPrice(product.price.toString());
          setDiscount(product.discount.toString());
          setBulkDiscountPrice(product.bulkDiscountPrice?.toString() || '0.00');
          setTaxRate(product.tax?.toString() || '0');
          setOffer(product.offer || '');
          setStockQuantity(product.stockQuantity.toString());
          setTypeQuantity(product.typeQuantity.toString());
          setStatus(product.status);
          
          if (product.imageUrl) {
            setImageUrl(product.imageUrl);
            setImagePreview(`https://ayushvatika.com${product.imageUrl}`);
          }

          setSelectedCategory(product.category);

          // Handle Type & Units properly
          if (product.categoryType) {
            setSelectedCategoryType(product.categoryType);
            
            // Get Units using Static Logic
            const units = getUnitsForType(product.categoryType.name);
            setMeasurementUnits(units);

            // Set Unit from DB if available in list
            if (product.measurementUnit) {
                // Check if DB unit matches any in the list (case-insensitive)
                const matchedUnit = units.find(u => u.toLowerCase() === product.measurementUnit.toLowerCase());
                if (matchedUnit) {
                    setSelectedMeasurementUnit(matchedUnit);
                } else if (units.length > 0) {
                    // Fallback to first if mismatch
                    setSelectedMeasurementUnit(units[0]);
                } else {
                    setSelectedMeasurementUnit(product.measurementUnit); // Keep original even if not in list
                }
            } else if (units.length > 0) {
               setSelectedMeasurementUnit(units[0]);
            }
          }
        }
      } catch (err) {
        showNotification('Failed to load data', 'error');
        console.error(err);
      }
    };
    initData();
  }, [id]);


  // --- Handlers ---
  const handleCategoryTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const typeId = parseInt(e.target.value);
    const type = categoryTypes.find(t => t.id === typeId) || null;
    
    setSelectedCategoryType(type);
    setSelectedMeasurementUnit(''); 

    if (type) {
        // Use the same static logic
        const units = getUnitsForType(type.name);
        setMeasurementUnits(units);
        
        // Auto-select first unit
        if (units && units.length > 0) {
            setSelectedMeasurementUnit(units[0]);
        }
    } else {
        setMeasurementUnits([]);
    }
  };

  const handleUpdateProduct = async () => {
    let finalImageUrl = imageUrl;

    try {
      // 1. Image Upload
      if (imageFile) {
        const formData = new FormData();
        formData.append('file', imageFile);
        const uploadRes = await fetch('https://ayushvatika.com/api/products/upload-image', { method: 'POST', body: formData });
        if (!uploadRes.ok) throw new Error('Image upload failed');
        finalImageUrl = await uploadRes.text();
      }

      // 2. Prepare Payload
      const payload: Product = {
        id: id ? parseInt(id) : undefined,
        productName,
        productCode,
        description,
        price: parseFloat(price),
        discount: parseFloat(discount),
        bulkDiscountPrice: parseFloat(bulkDiscountPrice),
        tax: parseFloat(taxRate),
        offer,
        imageUrl: finalImageUrl,
        category: selectedCategory!,
        categoryType: selectedCategoryType!,
        stockQuantity: parseInt(stockQuantity),
        typeQuantity: parseFloat(typeQuantity),
        measurementUnit: selectedMeasurementUnit,
        status,
      };

      console.log("Sending Payload:", payload); 

      const res = await fetch(`https://ayushvatika.com/api/products/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(await res.text());

      showNotification('Product updated successfully!', 'success');
      setTimeout(() => navigate('/admin/product/list'), 1500);
    } catch (error) {
      showNotification(`Update Failed: ${error}`, 'error');
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setImageFile(e.target.files[0]);
      setImagePreview(URL.createObjectURL(e.target.files[0]));
    }
  };

  // Styles (Updated for Dynamic Light/Dark Mode)
  const inputClass = "w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 shadow-inner text-sm transition-colors bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400";
  const labelClass = "block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1 uppercase tracking-wide";

  return (
    <AdminLayout>
      <div className="min-h-screen p-6 md:p-8 font-sans transition-colors bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center mb-4 md:mb-0">
            <FaEdit className="mr-3 text-green-600 dark:text-green-500" /> Edit Product
          </h1>
          <div className="flex gap-3">
             <button onClick={() => navigate('/admin/product/list')} className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 text-sm font-semibold transition-colors">
                Discard
             </button>
             <button onClick={handleUpdateProduct} className="px-6 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold shadow-lg transition-transform transform hover:scale-105">
                Save Changes
             </button>
          </div>
        </div>

        {notification && <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}

        {/* Main Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* LEFT COLUMN (2/3 width) - Details & Pricing */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Card 1: Basic Info */}
            <div className="rounded-xl p-6 shadow-lg border transition-colors bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700">
              <h2 className="text-xl font-semibold mb-4 flex items-center border-b pb-2 text-gray-900 dark:text-white border-gray-100 dark:border-gray-700">
                <FaClipboardList className="mr-2 text-blue-500" /> Basic Information
              </h2>
              <div className="space-y-4">
                <div>
                    <label className={labelClass}>Product Name <span className="text-red-500">*</span></label>
                    <input type="text" className={inputClass} placeholder="e.g. Alovera Juice" value={productName} onChange={(e) => setProductName(e.target.value)} />
                </div>
                <div>
                    <label className={labelClass}>Product Code</label>
                    <div className="relative">
                        <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 dark:text-gray-400"><FaBoxOpen /></span>
                        <input type="text" className={`${inputClass} pl-10`} placeholder="e.g. ALJC001" value={productCode} onChange={(e) => setProductCode(e.target.value)} />
                    </div>
                </div>
                <div>
                    <label className={labelClass}>Description</label>
                    <textarea className={`${inputClass} h-32 resize-none`} placeholder="Product details..." value={description} onChange={(e) => setDescription(e.target.value)} />
                </div>
              </div>
            </div>

            {/* Card 2: Pricing */}
            <div className="rounded-xl p-6 shadow-lg border transition-colors bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700">
              <h2 className="text-xl font-semibold mb-4 flex items-center border-b pb-2 text-gray-900 dark:text-white border-gray-100 dark:border-gray-700">
                <FaRupeeSign className="mr-2 text-yellow-500" /> Pricing
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className={labelClass}>Price (₹)</label>
                    <input type="text" className={inputClass} value={price} onChange={(e) => handleNumericChange(e, setPrice)} />
                </div>
                <div>
                    <label className={labelClass}>Discount (%)</label>
                    <input type="text" className={inputClass} value={discount} onChange={(e) => handleNumericChange(e, setDiscount)} />
                </div>
                <div>
                    <label className={labelClass}>Bulk Price (₹)</label>
                    <input type="text" className={inputClass} value={bulkDiscountPrice} onChange={(e) => handleNumericChange(e, setBulkDiscountPrice)} />
                </div>
                <div>
                    <label className={labelClass}>Tax Rate (%)</label>
                    <input type="text" className={inputClass} value={taxRate} onChange={(e) => handleNumericChange(e, setTaxRate)} />
                </div>
              </div>
              <div className="mt-4">
                  <label className={labelClass}>Offer Text</label>
                  <input type="text" className={inputClass} placeholder="e.g. Buy 1 Get 1 Free" value={offer} onChange={(e) => setOffer(e.target.value)} />
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN (1/3 width) - Organization & Media */}
          <div className="space-y-6">
            
            {/* Card 3: Organization */}
            <div className="rounded-xl p-6 shadow-lg border transition-colors bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700">
              <h2 className="text-xl font-semibold mb-4 flex items-center border-b pb-2 text-gray-900 dark:text-white border-gray-100 dark:border-gray-700">
                <FaLayerGroup className="mr-2 text-purple-500" /> Organization
              </h2>
              <div className="space-y-4">
                {/* Category */}
                <div>
                    <label className={labelClass}>Category <span className="text-red-500">*</span></label>
                    <select className={inputClass} value={selectedCategory?.id || ''} onChange={(e) => setSelectedCategory(categories.find(c => c.id === parseInt(e.target.value)) || null)}>
                        <option value="">Select Category</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>

                {/* Type - Updates Units */}
                <div>
                    <label className={labelClass}>Category Type <span className="text-red-500">*</span></label>
                    <select className={inputClass} value={selectedCategoryType?.id || ''} onChange={handleCategoryTypeChange}>
                        <option value="">Select Type</option>
                        {categoryTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                </div>

                {/* Combined Quantity per Unit and Measurement Unit */}
              <div className="mb-5">
                <label htmlFor="typeQuantity" className={labelClass}>Unit of Measurement</label>
                <div className="flex">
                  <input
                    type="text"
                    id="typeQuantity"
                    placeholder="e.g., 500"
                    className={`${inputClass} rounded-r-none border-r-0`}
                    style={{width: '65%'}}
                    value={typeQuantity}
                    onChange={(e) => handleNumericChange(e, setTypeQuantity)}
                  />
                  <select
                    id="measurementUnit"
                    className={`${inputClass} rounded-l-none border-l bg-gray-100 border-l-gray-300 dark:bg-gray-600 dark:border-l-gray-600`}
                    style={{width: '35%'}}
                    value={selectedMeasurementUnit}
                    onChange={(e) => setSelectedMeasurementUnit(e.target.value)}
                    disabled={!selectedCategoryType}
                  >
                    <option value="" className="bg-white dark:bg-gray-700">
                      Select Unit
                    </option>
                    {measurementUnits.map((unit) => (
                      <option key={unit} value={unit} className="bg-white dark:bg-gray-700">{unit}</option>
                    ))}
                  </select>
                </div>
              </div>

                {/* Stock & Status */}
                <div>
                    <label className={labelClass}>Stock Quantity</label>
                    <input type="text" className={inputClass} value={stockQuantity} onChange={(e) => handleNumericChange(e, setStockQuantity)} />
                </div>
                <div>
                    <label className={labelClass}>Status</label>
                    <select className={inputClass} value={status} onChange={(e) => setStatus(e.target.value)}>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                </div>
              </div>
            </div>

            {/* Card 4: Media */}
            <div className="rounded-xl p-6 shadow-lg border transition-colors bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700">
               <h2 className="text-xl font-semibold mb-4 flex items-center border-b pb-2 text-gray-900 dark:text-white border-gray-100 dark:border-gray-700">
                <FaImage className="mr-2 text-pink-500" /> Media
              </h2>
              <div className="border-2 border-dashed rounded-lg p-4 text-center transition-colors border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700/50 dark:hover:bg-gray-650">
                 {imagePreview ? (
                    <div className="relative w-full h-40 mb-3 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
                        <img src={imagePreview} alt="Preview" className="h-full object-contain" />
                    </div>
                 ) : (
                    <div className="text-gray-500 dark:text-gray-400 mb-3 text-sm">No image selected</div>
                 )}
                 
                 <label className="cursor-pointer inline-block bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors w-full">
                    <input type="file" className="hidden" accept="image/*" onChange={handleImageChange} />
                    {imagePreview ? 'Change Image' : 'Upload Image'}
                 </label>
              </div>
            </div>

          </div>
        </div>

      </div>
    </AdminLayout>
  );
};

export default EditProduct;